import { Component, computed, inject, resource } from '@angular/core';
import { SimpleApi } from '@core/api/simple.api';
import { RapPris } from '@features/produktlister/rap-pris.model';
import { DecimalPipe } from '@angular/common';
import { ProduktlisterService } from '@features/produktlister/produktlister.service';

@Component({
  selector: 'app-produktliste-result-rap',
  imports: [DecimalPipe],
  templateUrl: './produktliste-result-rap.component.html',
  styleUrl: './produktliste-result-rap.component.css',
})
export class ProduktlisteResultRapComponent {
  plService = inject(ProduktlisterService);
  simpleApi = inject(SimpleApi);

  priserResource = resource({
    params: () => ({
      aar: this.plService.aar(),
      produktTypeId: this.plService.produktType()?.id,
    }),
    loader: async ({ params, abortSignal }) => {
      return await this.simpleApi.getList<RapPris>(
        '/api/admin/produktlister/rappriser',
        {
          ...params,
        },
        abortSignal
      );
    },
  });

  priser = computed(() => this.priserResource.value() || []);

  getData(
    loebende: boolean,
    prisGruppe: number,
    aldersGruppe: AldersGruppe
  ): RapPris[] {
    return this.priser().filter(
      x =>
        x.loebende == loebende &&
        x.helbredsGruppe == prisGruppe &&
        x.minAlder == aldersGruppe.minAlder &&
        x.maxAlder == aldersGruppe.maxAlder
    );
  }

  getUdbetalingsTyper() {
    const list: boolean[] = [];
    this.priser().forEach(p => {
      if (!list.includes(p.loebende)) {
        list.push(p.loebende);
      }
    });
    return list;
  }

  getPrisgrupper() {
    const list: number[] = [];
    this.priser().forEach(p => {
      if (!list.includes(p.helbredsGruppe)) {
        list.push(p.helbredsGruppe);
      }
    });
    return list;
  }

  getAldersgrupper() {
    const list: AldersGruppe[] = [];
    this.priser().forEach(p => {
      if (p.minAlder || p.maxAlder) {
        let text = `Aldersgruppe ${p.minAlder} - ${p.maxAlder} år`;
        if (!p.minAlder) {
          text = `Aldersgruppe under ${p.maxAlder} år`;
        }
        if (!p.maxAlder) {
          text = `Aldersgruppe over ${p.minAlder} år`;
        }
        if (list.filter(x => x.text === text).length === 0) {
          list.push({
            minAlder: p.minAlder,
            maxAlder: p.maxAlder,
            text: text,
          });
        }
      }
    });
    if (list.length === 0) {
      list.push({
        text: 'Alle',
      });
    }
    return list;
  }

  getHelpText(produktTypeNummer: number, underKode: number) {
    const rap = produktTypeNummer + '-' + underKode;
    switch (rap) {
      case '1537-11':
        return 'Anvendes når 1537-xxx skal have ½ præmiefritagelse. Hel ved 2/3 lukket gruppe';
      case '1543-1':
        return 'Anvendes når 1537 skal sættes tilbage til udløb 60 år p.g.a. sygdom opstået inden konvertering til de nye grupper, uden præmiefritagelse';
      case '1543-11':
        return 'Anvendes når 1537 skal sættes tilbage til udløb 60 år p.g.a. sygdom opstået inden konvertering til de nye grupper, med ½ præmiefritagelse';
      case '1544-11':
        return 'Anvendes når kunder med 1523-xxx er blevet syg i perioden 2010 til 2012 og derfor ikke skal på de aldersopdelte priser.';
      case '1544-21':
        return 'Anvendes når kunder med 1523-xxx er blevet syg i perioden 2010 til 2012 og derfor ikke skal på de aldersopdelte priser.';
      case '1544-31':
        return 'Anvendes når kunder med 1523-xxx er blevet syg i perioden 2010 til 2012 og derfor ikke skal på de aldersopdelte priser.';
      case '1544-41':
        return 'Anvendes når kunder med 1523-xxx er blevet syg i perioden 2010 til 2012 og derfor ikke skal på de aldersopdelte priser.';
      case '1545-1':
        return 'Invalidesum der anvendes når 1535-xxx skal sættes tilbage til risikoophør 60 år.';
      case '1557-1':
        return 'TAE, Gammel LOPI 2010-2012 hel ved halv. Anvendes når en eksisterende 0267-xxx (hel ved halv) skal forblive på betingelser fra før 2010. (Risikoophør = 60 år)';
      case '1546-1':
        return 'TAE, Gammel LOPI 2010-2012 2/3 dele. Anvendes når en eksisterende 0267-xxx (2/3) skal forblive på betingelser for 2010-2012. (Risikoophør = 62 år)';
      case '1546-11':
        return 'TAE, Gammel LOPI 2010-2012 2/3 dele. Anvendes når en eksisterende 0267-xxx (2/3) skal forblive på betingelser for 2010-2012 og have ½ præmiefritagelse. (Risikoophør = 62 år)';
      case '1547-21':
        return 'TAE, Gammel LOPI 2010-2012 hel ved halv. Anvendes når en eksisterende 0267-xxx (hel ved halv) skal forblive på betingelser for 2010-2012. (Risikoophør = 62 år)';
      case '1563-231':
      case '1563-431':
        return 'Er en lukket gruppe, der anvendes til Sydbank bestand fra Topdanmark, som er oprettet pr. 1.1.2018';
    }
    return null;
  }
}

interface AldersGruppe {
  minAlder?: number;
  maxAlder?: number;
  text: string;
}
